import { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'react-toastify';
import { logger } from "../../../utils/logger";
import { WELCOME_MESSAGES, WELCOME_OPTIONS } from '../constants';

export function useMessageLoading({
  currentSessionId,
  user,
  fetchConversationMessages,
  contextMessages,
  hasFetched,
  cacheDisplayMessages,
  getCachedDisplayMessages
}) {
  // Lazy-initialise from the display-message cache (lives on ConversationProvider,
  // survives unmount/remount).  This is the primary source of truth for tab-switch
  // persistence because contextMessages (from useConversationSession) only contains
  // what was last fetched from the API — it does NOT include messages added during
  // the current chat session via processInput/sendMessage.
  const [messages, setMessages] = useState(() => {
    const cached = getCachedDisplayMessages(currentSessionId);
    if (cached && cached.length > 0) {
      logger.log("Restored", cached.length, "messages from display cache (tab-switch persistence)");
      return cached;
    }
    if (contextMessages && contextMessages.length > 0) {
      return contextMessages.map(msg => ({
        sender: msg.role === 'user' ? 'user' : 'assistant',
        text: msg.content,
        ...(msg.role === 'assistant' && msg.suggested_action ? { suggested_action: msg.suggested_action } : {}),
        ...(msg.role === 'assistant' && msg.options ? { options: msg.options } : {}),
        ...(msg.role === 'assistant' && msg.template_options ? { template_options: msg.template_options } : {})
      }));
    }
    return [];
  });
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [hasMoreMessages, setHasMoreMessages] = useState(false);

  const loadConversationMessages = useCallback(async (sessionId, page = 1, replace = true, force = false) => {
    if (!sessionId) return;

    setIsLoadingMessages(true);
    // Set synchronously before the await so a second effect in the same
    // render cycle (e.g. useSessionManagement) sees the guard immediately
    // and doesn't fire a duplicate call that returns [] and flashes the
    // welcome message before real messages arrive.
    hasFetched.current = true;
    try {
      // fetchConversationMessages now returns fetched data directly
      // (not stale closure state), so we can trust the result.
      // Pass force=true on tab-switch remount to bypass the
      // hasFetchedMessagesRef guard in useConversationSession.
      const fetched = await fetchConversationMessages(sessionId, force);
      logger.log("📥 Fetched messages:", fetched);

      if (fetched && Array.isArray(fetched) && fetched.length > 0) {
        const formattedMessages = fetched.map(msg => ({
          sender: msg.role === 'user' ? 'user' : 'assistant',
          text: msg.content,
          ...(msg.role === 'assistant' && msg.suggested_action ? { suggested_action: msg.suggested_action } : {}),
          ...(msg.role === 'assistant' && msg.options ? { options: msg.options } : {}),
          ...(msg.role === 'assistant' && msg.template_options ? { template_options: msg.template_options } : {})
        }));

        if (replace) {
          setMessages(formattedMessages);
        } else {
          setMessages(prev => [...formattedMessages, ...prev]);
        }
        
        setCurrentPage(1);
        setTotalPages(1);
        setHasMoreMessages(false);

      } else {
        // API returned empty — but if the display cache has messages (e.g. the
        // backend hasn't persisted them yet, or the session is very new), prefer
        // the cache over resetting to the welcome screen.
        const cached = getCachedDisplayMessages(sessionId);
        if (cached && cached.length > 0) {
          logger.log("📦 API returned empty but display cache has", cached.length, "messages — keeping cached state");
          setMessages(cached);
        } else {
          // Resumed session with no persisted messages — show the welcome
          // message so the user doesn't see an empty chat area.
          // Use functional update to avoid overwriting a guided greeting
          // that may have been set by checkForActiveWorkflow().
          logger.warn("⚠️ No messages returned for session — showing welcome");
          const welcome = WELCOME_MESSAGES[Math.floor(Math.random() * WELCOME_MESSAGES.length)];
          setMessages(prev => {
            if (prev.length > 0) return prev;
            return [{ sender: 'assistant', text: welcome, options: WELCOME_OPTIONS }];
          });
        }
      }
    } catch (err) {
      logger.error('Error loading conversation messages:', err);
      toast.error(`Failed to load conversation messages: ${err.message}`);
    } finally {
      setIsLoadingMessages(false);
    }
  }, [fetchConversationMessages, hasFetched, getCachedDisplayMessages]);

  useEffect(() => {
    if (currentSessionId && user && !hasFetched.current) {
      logger.log("🔄 TherapistPanel: Loading conversation messages");
      loadConversationMessages(currentSessionId);
    }
  }, [currentSessionId, user]);

  useEffect(() => {
    if (contextMessages && contextMessages.length > 0 && messages.length === 0) {
      logger.log("📥 Syncing context messages to local state:", contextMessages.length);
      const formattedMessages = contextMessages.map(msg => ({
        sender: msg.role === 'user' ? 'user' : 'assistant',
        text: msg.content,
        ...(msg.role === 'assistant' && msg.suggested_action ? { suggested_action: msg.suggested_action } : {}),
        ...(msg.role === 'assistant' && msg.options ? { options: msg.options } : {}),
        ...(msg.role === 'assistant' && msg.template_options ? { template_options: msg.template_options } : {})
      }));
      setMessages(formattedMessages);
    }
  }, [contextMessages, messages.length]);

  // Keep the display-message cache in sync so the next tab-switch remount
  // can restore messages immediately via the lazy initialiser above.
  // We track the previous session ID so that when a session transition occurs
  // we skip one cache write — this prevents writing stale messages from
  // session A under session B's key (e.g. when an in-flight AI response
  // from session A resolves after the user clicked "New Conversation").
  const prevSessionRef = useRef(currentSessionId);
  useEffect(() => {
    if (currentSessionId !== prevSessionRef.current) {
      prevSessionRef.current = currentSessionId;
      return; // session just changed — don't write stale messages
    }
    if (messages.length > 0) {
      cacheDisplayMessages(currentSessionId, messages);
    }
  }, [messages, currentSessionId, cacheDisplayMessages]);

  return {
    messages,
    setMessages,
    isLoadingMessages,
    currentPage,
    setCurrentPage,
    totalPages,
    hasMoreMessages,
    setHasMoreMessages,
    loadConversationMessages
  };
}
